import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, Sparkles, X } from 'lucide-react';
import { registerForPushNotifications } from '@/lib/fcm';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Push Permission Primer — soft pre-prompt sheet.
 *
 * Why: iOS / Android / web all fire a NATIVE permission dialog as soon as
 * `requestPermissions()` is called. If the user denies that native dialog,
 * we can never ask again — they have to dig into Settings. Industry standard
 * pattern is to show a soft "would you like push notifications? here's what
 * we'd send" preview FIRST, and only call the native prompt when the user
 * taps "Allow". This pushes opt-in rates from ~40% to ~75%+.
 *
 * Shown:
 *   - Once per user, after they finish onboarding (or on first session if
 *     already onboarded).
 *   - Skipped entirely if the browser already has permission granted/denied.
 *   - Dismissal sets a 7-day cool-off so we don't nag.
 */
export default function PushPrimerSheet() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user?.id) return;
    // Hard-skip rules.
    const lsKey = `gm_push_primer_${user.id}`;
    const dismissed = localStorage.getItem(lsKey);
    if (dismissed) {
      // Cool-off — only re-show after 7 days.
      const ts = parseInt(dismissed, 10);
      if (Number.isFinite(ts) && Date.now() - ts < 7 * 24 * 60 * 60 * 1000) return;
    }
    // If running in the browser and permission already resolved, skip.
    try {
      if (typeof Notification !== 'undefined') {
        if (Notification.permission === 'granted') {
          // Already granted — just (re)register silently.
          registerForPushNotifications().catch(() => {});
          return;
        }
        if (Notification.permission === 'denied') return; // can't ask again
      }
    } catch { /* ignore — likely SSR */ }
    // Defer to the next frame so the page paints first.
    const t = setTimeout(() => setOpen(true), 1200);
    return () => clearTimeout(t);
  }, [user?.id]);

  const remember = (action) => {
    if (!user?.id) return;
    localStorage.setItem(`gm_push_primer_${user.id}`, String(Date.now()));
    if (action === 'allow') {
      // Record a separate "definitive" key so we never re-prompt if granted.
      localStorage.setItem(`gm_push_primer_done_${user.id}`, '1');
    }
  };

  const onAllow = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await registerForPushNotifications();
    } catch { /* silent */ }
    remember('allow');
    setBusy(false);
    setOpen(false);
  };

  const onLater = () => {
    remember('later');
    setOpen(false);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center px-3 pt-3 pb-[max(env(safe-area-inset-bottom),0.75rem)]"
          onClick={onLater}
          data-testid="push-primer-overlay"
        >
          <motion.div
            initial={{ y: 60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 60, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 280, damping: 30 }}
            className="bg-white dark:bg-[#1E293B] rounded-3xl w-full max-w-md max-h-[85dvh] overflow-y-auto overflow-x-hidden"
            onClick={(e) => e.stopPropagation()}
            data-testid="push-primer-sheet"
          >
            <div
              className="relative px-5 pt-5 pb-4 text-white"
              style={{ background: 'radial-gradient(circle at 0% 0%, #7E5BEF 0%, #4F46E5 60%, #1E1B4B 100%)' }}
            >
              <span className="pointer-events-none absolute -top-8 -right-8 w-32 h-32 rounded-full bg-[#1CB0F6] opacity-30 blur-3xl" aria-hidden />
              <div className="relative flex items-start gap-3">
                <div className="w-12 h-12 rounded-2xl bg-white/15 border border-white/20 backdrop-blur flex items-center justify-center shadow-lg flex-shrink-0">
                  <Bell size={22} strokeWidth={3} />
                </div>
                <div className="flex-1">
                  <p className="text-[10px] uppercase tracking-widest font-extrabold opacity-90">One quick ask</p>
                  <h3 className="text-xl font-extrabold leading-tight">Stay on track with one tap</h3>
                </div>
                <button onClick={onLater} className="opacity-80 -mt-1 -mr-1" data-testid="push-primer-close">
                  <X size={20} strokeWidth={3} />
                </button>
              </div>
            </div>

            <div className="p-5 space-y-3">
              <PrimerRow icon="☀️" title="Today's mission" body="A gentle 8 AM nudge with the mission you set yourself." />
              <PrimerRow icon="📌" title="Roadmap due dates" body="The day before and the day a parked item is due." />
              <PrimerRow icon="🏆" title="Weekly challenge" body="When it launches, the day before it ends, and the last day." />
              <PrimerRow icon="👥" title="Your circle" body="One nightly digest from friends — never a spammy stream." />

              <p className="text-[11px] text-[#94A3B8] font-bold pt-1">
                You&rsquo;re in control — change any of these in Profile &rarr; Settings &rarr; Notifications.
              </p>

              <div className="flex gap-2 pt-1">
                <button
                  onClick={onLater}
                  disabled={busy}
                  className="flex-1 py-3 rounded-2xl bg-[#F1F5F9] dark:bg-[#0F172A] text-[#475569] dark:text-[#E2E8F0] text-sm font-extrabold disabled:opacity-50"
                  data-testid="push-primer-later"
                >
                  Maybe later
                </button>
                <button
                  onClick={onAllow}
                  disabled={busy}
                  className="flex-1 py-3 rounded-2xl text-white text-sm font-extrabold disabled:opacity-50 inline-flex items-center justify-center gap-1.5"
                  style={{ background: 'linear-gradient(135deg,#7E5BEF,#4F46E5)' }}
                  data-testid="push-primer-allow"
                >
                  <Sparkles size={14} strokeWidth={3} />
                  {busy ? 'Enabling…' : 'Turn on notifications'}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function PrimerRow({ icon, title, body }) {
  return (
    <div className="flex items-start gap-3">
      <span className="text-xl mt-0.5">{icon}</span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-extrabold">{title}</p>
        <p className="text-[11px] font-bold text-[#64748B] dark:text-[#94A3B8] leading-snug">{body}</p>
      </div>
    </div>
  );
}
